import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import EventMainReport from '@/model/EventMainReport';
import { EventPdvSaleProductReport } from '@/model/EventPdvSaleProductReport';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#333333',
    backgroundColor: '#FFFFFF',
  },
  header: {
    marginBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#1B4880',
    borderBottomStyle: 'solid',
    paddingBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1B4880',
  },
  headerSubtitle: {
    fontSize: 10,
    color: '#555555',
    marginTop: 2,
  },
  headerMeta: {
    fontSize: 8,
    color: '#666666',
    textAlign: 'right',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#1B4880',
    backgroundColor: '#F0F4F8',
    padding: '4 8',
    marginTop: 10,
    marginBottom: 6,
    borderRadius: 2,
  },
  subSectionTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#444444',
    marginTop: 6,
    marginBottom: 4,
  },
  table: {
    width: '100%',
    marginVertical: 4,
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#1B4880',
    color: '#FFFFFF',
    fontWeight: 'bold',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  tableRowEven: {
    backgroundColor: '#F9FBFD',
  },
  colName: { flex: 3 },
  colQty: { flex: 1, textAlign: 'right' },
  colMoney: { flex: 1.5, textAlign: 'right' },
  colStock: { flex: 1.2, textAlign: 'right' },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    borderTopWidth: 1,
    borderTopColor: '#EEEEEE',
    paddingTop: 6,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#888888',
  },
});

const formatCurrency = (val: number | undefined | null): string => {
  if (val === undefined || val === null) return 'R$ 0,00';
  return `R$ ${Number(val).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

interface ProductPdvPDFProps {
  event: EventMainReport;
  pdvs: EventPdvSaleProductReport[];
}

export const ProductPdvPDF: React.FC<ProductPdvPDFProps> = ({ event, pdvs }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Cabeçalho */}
      <View style={styles.header}>
        <View>
          <Text style={styles.headerTitle}>Relatório de Produtos por PDV</Text>
          <Text style={styles.headerSubtitle}>{event.name || 'Evento sem nome'}</Text>
        </View>
        <View style={styles.headerMeta}>
          <Text>Data Evento: {event.date ? dayjs(event.date).format('DD/MM/YYYY') : 'N/I'}</Text>
          <Text>Emissão: {dayjs().format('DD/MM/YYYY HH:mm')}</Text>
        </View>
      </View>

      {pdvs.map((pdv, pIdx) => (
        <View key={`pdv-${pIdx}`} style={{ marginBottom: 10 }}>
          <Text style={styles.sectionTitle}>
            PDV: {pdv.name} (Vendas: {formatCurrency(pdv.summary?.totalSaleValue)})
          </Text>

          {pdv.sections?.map((sec, sIdx) => (
            <View key={`psec-${sIdx}`} style={{ marginBottom: 6 }}>
              {sec.categories?.map((cat, cIdx) => (
                <View key={`pcat-${cIdx}`} style={{ marginBottom: 6 }}>
                  <Text style={styles.subSectionTitle}>
                    {sec.name} - {cat.name}
                  </Text>
                  <View style={styles.table}>
                    <View style={styles.tableHeaderRow}>
                      <Text style={styles.colName}>Produto/Combo</Text>
                      <Text style={styles.colQty}>Vendidos</Text>
                      <Text style={styles.colMoney}>V. Unit</Text>
                      <Text style={styles.colMoney}>Venda</Text>
                      <Text style={styles.colMoney}>Comissão</Text>
                      <Text style={styles.colStock}>Est. Inc.</Text>
                      <Text style={styles.colStock}>Est. Disp.</Text>
                    </View>

                    {cat.products?.map((prod, prIdx) => (
                      <View
                        key={`pprod-${prIdx}`}
                        style={[styles.tableRow, prIdx % 2 === 1 ? styles.tableRowEven : {}]}
                      >
                        <Text style={styles.colName}>{prod.name}</Text>
                        <Text style={styles.colQty}>{prod.amountSold}</Text>
                        <Text style={styles.colMoney}>{formatCurrency(prod.unitValue)}</Text>
                        <Text style={styles.colMoney}>{formatCurrency(prod.totalSaleValue)}</Text>
                        <Text style={styles.colMoney}>{formatCurrency(prod.totalCommission)}</Text>
                        <Text style={styles.colStock}>{prod.initialInventory || 0}</Text>
                        <Text style={styles.colStock}>{prod.availableStock || 0}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ))}
            </View>
          ))}
        </View>
      ))}

      {/* Rodapé Fixo */}
      <View style={styles.footer} fixed>
        <Text>Banca do Ingresso - Relatório de Produtos por PDV</Text>
        <Text render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
      </View>
    </Page>
  </Document>
);
